import { useCallback, useSyncExternalStore } from 'react';

/**
 * "Larger text" preference for users who find the default size hard to read.
 * MUI sizes everything in rem, so scaling the root font size enlarges text, inputs and
 * buttons together without breaking layouts. Stored per device.
 */
const KEY = 'pd.textSize';
const LARGE_ROOT_SIZE = '118.75%'; // 19 px instead of 16 px

export type Size = 'normal' | 'large';
const listeners = new Set<() => void>();

function read(): Size {
  try {
    return localStorage.getItem(KEY) === 'large' ? 'large' : 'normal';
  } catch {
    return 'normal';
  }
}

export function applyTextSize(size: Size = read()) {
  document.documentElement.style.fontSize = size === 'large' ? LARGE_ROOT_SIZE : '';
}

export function useTextSize() {
  const size = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    read,
    () => 'normal' as Size,
  );
  const set = useCallback((next: Size) => {
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* private mode: the change still applies for this visit */
    }
    applyTextSize(next);
    listeners.forEach((l) => l());
  }, []);
  const toggle = useCallback(() => set(read() === 'large' ? 'normal' : 'large'), [set]);
  return { large: size === 'large', set, toggle };
}
